import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { QtyControl, AddToCartStyled } from './AddToCart.styled';
import { Text } from 'ui-redesign/elements';

import { addItems, removeItems, updateItems, getItemInCart } from 'redux/cartCheckout/cartCheckout';
import { useBreakpoint } from 'utils/hooks';
import { formatDynamicDecimalPrice } from 'utils/cart-utils';

const AddToCart = ({
  item,
  label,
  isDisabled,
  isSoldOut,
  isOutOfSeason,
  outOfSeasonLabel,
  isLoading,
  showPrice,
  onAddItem,
  onFirstAdd,
  modifiers,
}) => {
  const cartItem = useSelector((state) => getItemInCart(state, item.shopifyVariantId));
  const reservedItem = useSelector((state) => state.reservation.items.find((resItem) => resItem.sku === item.sku));
  const dispatch = useDispatch();
  const size = useBreakpoint();
  const itemQty = (cartItem && cartItem.quantity) || 0;
  const hasAnyItems = itemQty > 0;
  const soldOutLabel = isOutOfSeason ? outOfSeasonLabel : 'SOLD OUT';
  const isSoldOutOrOutOfSeason = isSoldOut || isOutOfSeason;

  const addToCart = () => {
    if (isDisabled) return;
    onAddItem && onAddItem();
    onFirstAdd && onFirstAdd();
    dispatch(addItems({ items: [{ variantId: item.shopifyVariantId, quantity: 1 }] }));
  };

  const incrementQty = () => {
    if (isDisabled) return;
    const itemToUpdate = {
      id: cartItem.id,
      quantity: cartItem.quantity + 1,
    };

    onAddItem && onAddItem();
    dispatch(updateItems({ items: [itemToUpdate] }));
  };

  const decrementQty = () => {
    if (isDisabled) return;

    if (cartItem.quantity === 1) {
      dispatch(removeItems({ items: [cartItem.id] }));
    } else {
      const itemToUpdate = {
        id: cartItem.id,
        quantity: cartItem.quantity - 1,
      };
      dispatch(updateItems({ items: [itemToUpdate] }));
    }
  };
  return (
    <>
      {hasAnyItems && !isLoading ? (
        <QtyControl
          onDecrease={decrementQty}
          onIncrease={incrementQty}
          disabledIncrease={reservedItem && reservedItem.max && itemQty >= reservedItem.max}
          quantity={itemQty}
          isActive={!isDisabled && (!isSoldOutOrOutOfSeason || !!reservedItem)}
          modifiers={size === 'SM' && 'fluid'}
        />
      ) : (
        <AddToCartStyled
          label={
            isSoldOutOrOutOfSeason && !reservedItem
              ? soldOutLabel
              : isLoading
              ? '...ADDING TO CART'
              : `${label} ${showPrice && item.priceCents ? formatDynamicDecimalPrice(item.priceCents) : ''}`
          }
          onClick={addToCart}
          modifiers={[
            ...modifiers,
            size === 'SM' && 'fluid',
            (isDisabled || (isSoldOutOrOutOfSeason && !reservedItem) || isLoading) && 'disabled',
          ]}
        />
      )}
      {reservedItem && reservedItem.max && <Text modifiers={['small', 'errorColor']}>{`Maximum of ${reservedItem.max}`}</Text>}
    </>
  );
};

AddToCart.defaultProps = {
  isDisabled: false,
  isLoading: false,
  showPrice: true,
  item: {},
  onAddItem: null,
  onFirstAdd: null,
  isSoldOut: false,
  isOutOfSeason: false,
  outOfSeasonLabel: 'OUT OF SEASON',
  label: 'ADD TO CART',
  modifiers: [],
};

AddToCart.propTypes = {
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  showPrice: PropTypes.bool,
  item: PropTypes.shape({
    sku: PropTypes.string,
    qty: PropTypes.number,
    priceCents: PropTypes.number,
  }),
  onAddItem: PropTypes.func,
  onFirstAdd: PropTypes.func,
  isSoldOut: PropTypes.bool,
  isOutOfSeason: PropTypes.bool,
  outOfSeasonLabel: PropTypes.string,
  label: PropTypes.string,
  modifiers: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
};

export default AddToCart;
